import { useState } from 'react'
import { getAvatarColor, getInitials } from '../../utils/avatar-color'

interface AvatarProps {
  name: string
  /** URL da foto de perfil (Google ou Microsoft). Faz fallback para iniciais coloridas. */
  picture?: string | null
  /** Tamanho em pixels (lado do quadrado). Default 28 (h-7 w-7). */
  size?: number
  /** Classe extra (ex.: ring para destaque). */
  className?: string
  title?: string
}

/**
 * Avatar com foto do Google/Microsoft + fallback para iniciais coloridas
 * (deterministic por nome). Mesmo padrão visual usado no DetailsHeader principal.
 *
 * Por que normalizar a URL aqui: o backend devolve `google_picture` como
 * `https://lh3.googleusercontent.com/...` que rejeita hot-link sem `sz=`. Ajustar
 * uma vez aqui evita repetir em cada call site.
 */
export function Avatar({
  name,
  picture,
  size = 28,
  className = '',
  title,
}: AvatarProps) {
  const [imgFailed, setImgFailed] = useState(false)
  const showImage = !!picture && !imgFailed

  const normalizedSrc = (() => {
    if (!picture) return undefined
    if (picture.includes('googleusercontent.com')) {
      const baseUrl = picture.split('?')[0]
      return `${baseUrl}?sz=${Math.max(64, size * 2)}&d=404`
    }
    return picture
  })()

  if (showImage && normalizedSrc) {
    return (
      <img
        src={normalizedSrc}
        alt={name}
        title={title ?? name}
        onError={() => setImgFailed(true)}
        style={{ width: size, height: size }}
        className={`shrink-0 rounded-full object-cover ${className}`}
      />
    )
  }

  const initials = getInitials(name)
  const color = getAvatarColor(name)
  return (
    <div
      title={title ?? name}
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        fontSize: Math.max(10, Math.round(size * 0.36)),
      }}
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold text-white ${className}`}
      aria-hidden
    >
      {initials}
    </div>
  )
}
